<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <Alert v-if="error" variant="error" :message="error" class="mb-4" />
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <OrganizationAvatar :organization="organization" size="xl" />
            <div>
              <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
                {{ organization?.name || 'Organization Settings' }}
              </h1>
              <p class="text-gray-600 dark:text-gray-300">
                Manage your organization, team members, and verification.
              </p>
            </div>
          </div>
          <div class="flex space-x-3">
            <div
              v-if="isLoading"
              class="w-48 h-24 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-xl"
            ></div>
            <CreditBalanceCard v-else-if="organization" :credits="credits" @top-up="handleTopUp" />
          </div>
        </div>
      </div>

      <!-- Navigation Tabs -->
      <div class="border-b border-gray-200 dark:border-gray-600 mb-8">
        <nav class="-mb-px flex space-x-8 overflow-x-auto">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="onTabChange(tab.id)"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors flex items-center',
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500',
            ]"
          >
            <component :is="tab.icon" class="w-5 h-5 mr-2" />
            {{ tab.name }}
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="space-y-8">
        <div v-if="activeTab === 'general'">
          <OrganizationGeneralSettings />
        </div>

        <div v-if="activeTab === 'members'">
          <OrganizationMembersList @invite="activeTab = 'invites'" />
        </div>

        <div v-if="activeTab === 'invites'">
          <OrganizationInviteList @invite="showInviteModal = true" />
        </div>

        <div v-if="activeTab === 'domain'">
          <OrganizationDomainSettings />
        </div>

        <div v-if="activeTab === 'scheduler'">
          <OrganizationSchedulerSettings />
        </div>

        <div v-if="activeTab === 'system-models'">
          <SystemModelsSection />
        </div>
      </div>

      <!-- Modals -->
      <InviteMemberModal v-model:open="showInviteModal" @invited="handleInvited" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import {
    Cog6ToothIcon,
    UsersIcon,
    EnvelopeIcon,
    GlobeAltIcon,
    QueueListIcon,
    CreditCardIcon,
    CpuChipIcon,
  } from '@heroicons/vue/24/outline';
  import { Alert } from '@asabdev/asab-ui';
  import { ROUTE_NAMES } from '@/router/types';
  import type { OrganizationDetail } from '../types';
  import {
    OrganizationAvatar,
    CreditBalanceCard,
    OrganizationGeneralSettings,
    OrganizationMembersList,
    OrganizationInviteList,
    OrganizationDomainSettings,
    OrganizationSchedulerSettings,
    SystemModelsSection,
    InviteMemberModal,
  } from '../components';
  import { useOrganization, useOrganizationInvites } from '../composables';

  const router = useRouter();
  const { organization, initialize, isLoading, error } = useOrganization();
  const { fetchInvitations: fetchInvites } = useOrganizationInvites();

  const activeTab = ref('general');
  const showInviteModal = ref(false);

  const credits = computed(() => (organization.value as OrganizationDetail)?.credits || null);

  const handleTopUp = () => {
    if (organization.value?.id) {
      router.push({
        name: ROUTE_NAMES.ORGANIZATION_CREDITS,
        params: { id: organization.value.id },
      });
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: Cog6ToothIcon },
    { id: 'members', name: 'Team Members', icon: UsersIcon },
    { id: 'invites', name: 'Invites', icon: EnvelopeIcon },
    { id: 'domain', name: 'Domain', icon: GlobeAltIcon },
    { id: 'scheduler', name: 'Scheduler', icon: QueueListIcon },
    { id: 'system-models', name: 'System Models', icon: CpuChipIcon },
    { id: 'billing', name: 'Billing', icon: CreditCardIcon },
  ];

  // Billing lives on its own route (deep-linkable, ORG_BILLING). Selecting
  // the tab routes there rather than rendering inline, mirroring the Top-up
  // credits navigation.
  function onTabChange(id: string) {
    if (id === 'billing' && organization.value?.id) {
      router.push({
        name: ROUTE_NAMES.ORGANIZATION_BILLING,
        params: { id: organization.value.id },
      });
      return;
    }
    activeTab.value = id;
  }

  const handleInvited = () => {
    fetchInvites();
    activeTab.value = 'invites';
  };

  onMounted(async () => {
    await initialize();
  });
</script>
